import React, { useState, useRef, useEffect } from 'react';
import { editUserProfile, profilePhotoUpload, profileCoverPhotoUpload } from '../../../services/userService';
import AddJobPanel from '../../../components/panels/ServiceRequestPanel';
import '../../../css/ownprofile.css';
import { Outlet, useNavigate } from 'react-router-dom';
import Resizer from 'react-image-file-resizer';
import { cropAndResizeProfileImage, cropAndResizeCoverImage } from '../../../utils/imageUtils';
import { renderStars } from '../../../utils/renderStars';
import { useAuth } from '../../../context/AuthContext';
import { useDispatch } from "react-redux";
import { patchProfile } from '../../../store/profileSlice';
import EditProfileModal from '../../../components/modals/EditProfileModal';
import { getImage } from '../../../utils/cloudinarySizing';

const OwnProfile = ({ profileData, subscriptions, setProfileData, projects, loadingMedia }) => {
    const [isOverlayOpen, setOverlayOpen] = useState(false);
    const [previewPhoto, setPreviewPhoto] = useState(null);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [showAddMedia, setShowAddMedia] = useState(false);
    const [selectedReview, setSelectedReview] = useState(null);
    const [nameUpdated, setNameUpdated] = useState(null);
    const [editForm, setEditForm] = useState({
        name: profileData?.profile?.first_name || '',
        bio: profileData?.bio || '',
        photo_original: profileData?.photo_original || "/servspace-default-500x500.png",
    });

    const { role, displayName, setDisplayName, setProfileThumbnail } = useAuth();

    const navigate = useNavigate();

    const dispatch = useDispatch();

    const toggleDropdown = (index) => {
        setActiveDropdown(activeDropdown === index ? null : index);
    };

    const count = subscriptions.length

    const fileInputRef = useRef(null); // Ref for cover photo upload input


    const handleOverlayToggle = () => {
        setOverlayOpen(!isOverlayOpen);
    };

    const handleEdit = () => {
        setEditForm({
            name: editForm.name, bio: profileData?.bio, photo: profileData?.photo_original,
            cover_image: profileData?.cover_image,
        });
        setIsEditModalOpen(true);
    };


    const handlePhotoChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("photo_original", file);

        try {
            const response = await profilePhotoUpload(formData);

            const { photo_url } = response.data;

            // 🔥 1. Update header instantly
            if (photo_url) {
                setProfileThumbnail(photo_url);
                localStorage.setItem("avatar", photo_url);
            }

            // 🔥 2. Update Redux profile instantly
            dispatch(
                patchProfile({
                    profile: {
                        ...profileData.profile,
                        photo_original: photo_url,
                    },
                    photo_url,
                })
            );

            // 🔥 3. Optional preview (same image now)
            setPreviewPhoto(photo_url);

            alert(response.data.message);
        } catch (error) {
            console.error("Error uploading profile photo:", error);
            alert("Failed to upload profile photo. Please try again.");
        }
    };

    const handleProfileUpdate = async (e) => {
        e.preventDefault();

        const formData = new FormData();
        formData.append('first_name', editForm.name);
        formData.append('bio', editForm.bio);

        if (
            editForm.photo_original &&
            typeof editForm.photo_original !== 'string'
        ) {
            formData.append('photo_original', editForm.photo_original);
        }

        try {
            const updatedProfile = await editUserProfile(formData);

            const updatedName = updatedProfile?.first_name || editForm.name;

            // update name
            setDisplayName(updatedName);
            localStorage.setItem('first_name', updatedName);

            setNameUpdated(updatedName);

            setEditForm(prev => ({
                ...prev,
                name: updatedName
            }));

            // ✅ SINGLE IMAGE ONLY
            if (updatedProfile.photo_original) {
                setProfileThumbnail(updatedProfile.photo_original);
                localStorage.setItem("avatar", updatedProfile.photo_original);
            }

            // ✅ Redux patch (simplified)
            dispatch(
                patchProfile({
                    profile: {
                        ...profileData.profile,
                        ...updatedProfile,
                    }
                })
            );

            setIsEditModalOpen(false);
            alert('Profile updated successfully');

        } catch (error) {
            console.error('Failed to update profile:', error);

            const message =
                error?.response?.data?.message ||
                error?.response?.data?.detail ||
                error?.response?.data?.error ||
                'An unknown error occurred.';

            alert(`Failed to update profile: ${message}`);
        }
    };

    return (
        <div className="page-wrapper">
            <div className="profile-container">
                <div className='centered-content'>
                    <div className="profile-content">
                        <div className="left-side">
                            <div className='photo-and-reviews'>
                                <div className="profile-photo">
                                    <img
                                        onClick={handleOverlayToggle}
                                        src={getImage(
                                            profileData?.profile?.photo_original || previewPhoto,
                                            "avatar_sm"
                                        )}
                                        alt="Profile"
                                    />
                                    <div className="edit-profile-photo"
                                        onClick={() => document.getElementById('file-input').click()} >
                                        {/* Hidden input, camera icon will trigger this */}
                                        <input
                                            type="file"
                                            onChange={handlePhotoChange}
                                            style={{ display: 'none' }}
                                            id="file-input"
                                        />
                                        <label className="camera-icon">
                                            <i className="fa-solid fa-camera"></i>
                                        </label>
                                    </div>
                                </div>

                                {/* Overlay for Enlarged Image */}
                                {isOverlayOpen && (
                                    <div className="profile-image-overlay" onClick={handleOverlayToggle}>
                                        {profileData ? (
                                            <img
                                                onClick={handleOverlayToggle}
                                                src={getImage(
                                                    profileData?.profile?.photo_original || previewPhoto,
                                                    "avatar_lg"
                                                )}
                                                alt="Profile"
                                            />
                                        ) : (
                                            <p>Loading...</p>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="profile-user-info">
                                <div className="user-text">
                                    <h3>
                                        {profileData?.profile?.first_name ||
                                            profileData?.profile?.username}
                                    </h3>
                                </div>


                            </div>
                        </div>
                        <div className="right-side">
                            <button onClick={handleEdit} className="edit-button">
                                <i className="fa-solid fa-pencil"></i>
                            </button>
                        </div>
                    </div>
                    <Outlet
                        context={{
                            profileData,
                            subscriptions,
                            setProfileData,
                            toggleDropdown,
                            loadingMedia,
                            showAddMedia,
                            setShowAddMedia,
                            activeDropdown,
                            setActiveDropdown,
                            setSelectedReview
                        }}
                    />
                </div>
            </div>
            <EditProfileModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                profileData={profileData}
                editForm={editForm}
                setEditForm={setEditForm}
                handleProfileUpdate={handleProfileUpdate}
            />
        </div>

    );

}

export default OwnProfile;